import React, { useState, useRef, useEffect } from 'react';
import { PaperAirplaneIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatbotProps {
  onBack: () => void;
}

const CHAT_HISTORY_KEY = 'certs_chat_history';

// Check if support is online (9 AM - 6 PM, Monday-Friday)
const isSupportOnline = (): boolean => {
  const now = new Date();
  const hour = now.getHours();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Monday to Friday (1-5), 9 AM to 6 PM
  return day >= 1 && day <= 5 && hour >= 9 && hour < 18;
};

// Get welcome message based on support availability
const getWelcomeMessage = (): string => {
  if (isSupportOnline()) {
    return "Hi! I'm here to help you with any questions about the platform. Our support team is online and ready to assist you. What can I help you with today?";
  } else {
    return "Hi! I'm here to help you with any questions about the platform. Our support team is currently offline (available 9 AM - 6 PM, Monday-Friday), but I can provide automated assistance. What can I help you with?";
  }
};

export default function Chatbot({ onBack }: ChatbotProps) {
  // Load chat history from localStorage
  const loadChatHistory = (): Message[] => {
    try {
      const saved = localStorage.getItem(CHAT_HISTORY_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
    
    // Default welcome message
    return [
      {
        id: '1',
        text: getWelcomeMessage(),
        isUser: false,
        timestamp: new Date()
      }
    ];
  };

  const [messages, setMessages] = useState<Message[]>(loadChatHistory());
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Save chat history to localStorage
  const saveChatHistory = (newMessages: Message[]) => {
    try {
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(newMessages));
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    saveChatHistory(newMessages);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getBotResponse(inputValue),
        isUser: false,
        timestamp: new Date()
      };
      const finalMessages = [...newMessages, botResponse];
      setMessages(finalMessages);
      saveChatHistory(finalMessages);
      setIsTyping(false);
    }, 1500);
  };

  const getBotResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    
    // Job-related questions
    if (input.includes('job') && input.includes('create')) {
      return "To create a new job:\n\n1. Click 'Create Job' on the Dashboard or Jobs page\n2. Fill in job details (title, type, priority)\n3. Assign an engineer and schedule\n4. Select or add customer information\n5. Choose or add a site location\n6. Review and confirm\n\nThe system will guide you through each step with validation to ensure all required information is provided.";
    }
    
    if (input.includes('job') && (input.includes('status') || input.includes('track'))) {
      return "You can track job status from the Dashboard or Jobs page. Jobs progress through these stages:\n\n• Created → Scheduled → In Progress → Completed → Certified\n\nEach job shows its current status, assigned engineer, and scheduled date. Click on any job to view detailed information.";
    }
    
    // Certificate-related questions
    if (input.includes('certificate') || input.includes('cert')) {
      return "Certificate generation:\n\n1. Navigate to the Certificates page\n2. Click 'New Certificate'\n3. Select a completed job\n4. Review certificate details\n5. Generate and download PDF\n\nCertificates can only be generated for jobs marked as 'Completed'. The system supports Gas Safety, Electrical, and Fire Safety certificates.";
    }
    
    // Customer/Client questions
    if (input.includes('customer') || input.includes('client')) {
      return "Customer management features:\n\n• Add new customers during job creation or from the Clients page\n• Each customer can have multiple sites\n• Store contact details, company information, and service history\n• Search and filter customers easily\n• Import/export customer data\n\nCustomers are automatically linked to their jobs and certificates for easy tracking.";
    }
    
    // Site-related questions
    if (input.includes('site') || input.includes('location')) {
      return "Site management:\n\n• Each customer can have multiple sites (Office, Warehouse, Retail, etc.)\n• Store site addresses, access instructions, and key holder details\n• Add sites during job creation or manage them from customer profiles\n• Track job history per site\n• Include site-specific requirements and notes";
    }
    
    // Engineer/scheduling questions
    if (input.includes('engineer') || input.includes('schedule')) {
      return "Engineer assignment and scheduling:\n\n• View engineer availability in real-time\n• Balance workload across your team\n• Set priority levels for urgent jobs\n• Engineers receive automatic notifications\n• Calendar integration available\n• Track engineer performance and job completion rates";
    }
    
    // Dashboard questions
    if (input.includes('dashboard')) {
      return "The Dashboard provides:\n\n• Overview of active jobs and their status\n• Key performance metrics and trends\n• Quick action buttons (Create Job, New Certificate)\n• Recent activity feed\n• Team productivity insights\n• Upcoming scheduled jobs\n\nIt's your central hub for monitoring business operations.";
    }
    
    // General help
    if (input.includes('help') || input.includes('support') || input.includes('how')) {
      return "I'm here to help! Here are some ways to get assistance:\n\n• Ask me specific questions about features\n• Check our Documentation for detailed guides\n• Watch video tutorials in Help Guides\n• Use 'Get Started' for a platform walkthrough\n• Contact support during business hours (9 AM - 6 PM)\n\nWhat specific feature would you like to know more about?";
    }
    
    // Default response with suggestions
    return "I'd be happy to help! I can assist with questions about:\n\n• Creating and managing jobs\n• Generating certificates\n• Customer and site management\n• Engineer scheduling\n• Dashboard features\n• Platform navigation\n\nTry asking something like 'How do I create a job?' or 'How do I generate a certificate?'";
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chatbot-popup-content">
      {/* Header */}
      <div className="chatbot-popup-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        <div className="chatbot-title">
          <h3>Support Chat</h3>
          <span className={`status-indicator ${isSupportOnline() ? 'online' : 'offline'}`}>
            {isSupportOnline() ? 'Online' : 'Offline'}
          </span>
        </div>
      </div>

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.isUser ? 'user' : 'bot'}`}
            >
              <div className="message-content">
                {message.text}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="message bot">
              <div className="message-content typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

      {/* Input */}
      <div className="chatbot-popup-input">
        <div className="input-container">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="message-input"
            rows={1}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}
            className="send-button"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
